import { useDispatch } from "react-redux";
import Types from "Types";
import {
  useCalculLoyerMax,
  useForcerStatutDossier,
  useGetSyntheseDossier,
  useVerifierDossierLocation,
} from "../../../../hooks/DossierLocation";
import { Selectors as AdministrateurBienActionSelectors } from "../../../../redux/AdministrateurDeBien";
import {
  ActionCreators as DossierLocationActionCreators,
  Selectors as DossierLocationSelectors,
} from "../../../../redux/DossierLocation";
import {
  ActionCreators as PartenaireActionCreators,
  Selectors as PartenaireSelectors,
} from "../../../../redux/Partenaire";
import { ActionCreators as PiecesActionCreators } from "../../../../redux/Pieces";
import { ActionCreators as SituationPersoActionCreators } from "../../../../redux/SituationPersonnelle";
import { ActionCreators as SituationProActionCreators } from "../../../../redux/SituationProfessionnelle";
import { Selectors as UtilisateursSelectors } from "../../../../redux/Utilisateurs";

import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useGetPartenaireParCode } from "../../../../hooks/Partenaire";
import { ActionCreators as BienActionCreators } from "../../../../redux/Bien";
import { InlineObjectStatutEnum } from "../../../../services/Api/swagger";

export const useSynthese = () => {
  const {
    mutate: mutateSynthese,
    isLoading: isFetchingSynthese,
    error: errorSynthese,
  } = useGetSyntheseDossier();
  const getSynthese = (ref: string) => {
    mutateSynthese(ref);
  };

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const resetSituationPro = () =>
    dispatch(SituationProActionCreators.resetSituationProParcoursCreation());
  const addColoc = (ref: string) => {
    if (ref) {
      resetSituationPro();
      resetSelectedPerson();
      dispatch(
        SituationPersoActionCreators.resetSituationPersoAndAddColoc(ref)
      );
      navigate("/dossier-location/situation-personnelle");
    }
  };
  const addLocataire = (ref: string) => {
    if (ref) {
      resetSituationPro();
      resetSelectedPerson();
      dispatch(
        SituationPersoActionCreators.resetSituationPersoAndAddLocataire(ref)
      );
      navigate("/dossier-location/situation-personnelle");
    }
  };

  const setSelectedPerson = (ref: string) =>
    dispatch(DossierLocationActionCreators.setSelectedPerson(ref));
  const resetSelectedPerson = () =>
    dispatch(DossierLocationActionCreators.resetSelectedPerson());
  const resetParcours = () => {
    dispatch(BienActionCreators.resetBienParcoursCreation());
    dispatch(
      SituationPersoActionCreators.resetSituationPersonnelleParcoursCreation()
    );
    dispatch(SituationProActionCreators.resetSituationProParcoursCreation());
    dispatch(PiecesActionCreators.resetToken());
    dispatch(PartenaireActionCreators.resetPartenaire());
  };

  const {
    mutate: mutateCalculLoyerMax,
    isSuccess: isSuccessCalculLoyerMax,
    isLoading: calculLoyerFetching,
    error: calculLoyerError,
  } = useCalculLoyerMax();
  const calculLoyerMax = (ref?: string) => {
    ref && mutateCalculLoyerMax(ref);
  };

  const { mutate: mutateForcerStatutDossier, isLoading: forcerStatutFetching } =
    useForcerStatutDossier();
  const forcerStatutDossier = (ref: {
    ref: string;
    statut: InlineObjectStatutEnum;
    message: string;
  }) => {
    mutateForcerStatutDossier(ref);
  };

  const { mutate: mutatePartenaire, isLoading: isFetchingPartenaire } =
    useGetPartenaireParCode();
  const getPartenaire = (code: string) => {
    mutatePartenaire(code);
  };

  const synthese = useSelector((state: Types.RootState) =>
    DossierLocationSelectors.getDossierLocation(state)
  );
  const compositionLocation = useSelector((state: Types.RootState) =>
    DossierLocationSelectors.getCompositionLocation(state)
  );

  const selectedDossier = useSelector((state: Types.RootState) =>
    DossierLocationSelectors.getSelectedDossier(state)
  );

  const infoProfil = useSelector((state: Types.RootState) =>
    UtilisateursSelectors.getProfilUtilisateur(state)
  );

  const utilisateurConnecte = useSelector((state: Types.RootState) =>
    UtilisateursSelectors.getUtilisateurConnecte(state)
  );
  const isControleDossier = useSelector((state: Types.RootState) =>
    PartenaireSelectors.getControleDossier(state)
  );
  const partenaire = useSelector((state: Types.RootState) =>
    PartenaireSelectors.getPartenaire(state)
  );

  const adbDetail = useSelector((state: Types.RootState) =>
    AdministrateurBienActionSelectors.getADBDetail(state)
  );
  const [infoSaisiesOk, setInfoSaisiesOk] = useState<boolean>(false);
  const [infoAVerifier, setInfoAVerifier] = useState<boolean>(false);

  useEffect(() => {
    if (synthese?.parcoursCandidatLocataire) setInfoSaisiesOk(true);
    else setInfoSaisiesOk(false);
  }, [synthese?.parcoursCandidatLocataire]);

  useEffect(() => {
    if (synthese?.statut === "A_VERIFIER") setInfoAVerifier(true);
    else setInfoAVerifier(false);
  }, [synthese?.statut]);
  const refPath = new URLSearchParams(document.location.search).get(
    "refDossier"
  );
  useEffect(() => {
    if (refPath) {
      getSynthese(refPath);
    } else if (selectedDossier) {
      getSynthese(selectedDossier);
    }

    resetParcours();
  }, []);

  useEffect(() => {
    if (synthese && synthese.partenaire && !partenaire) {
      // Une fois la synthèse chargée, on charge le partenaire le cas échéant
      getPartenaire(synthese.partenaire.codePartenaire);
    }
  }, [partenaire, synthese]);

  const handleForcerStatutDossier = (params: {
    statut: InlineObjectStatutEnum;
    message: string;
  }) => {
    if (synthese && synthese.reference) {
      const parameters = {
        ref: synthese.reference,
        ...params,
      };
      forcerStatutDossier(parameters);
    }
  };

  const handleClickInfoSaisies = () => {
    setInfoSaisiesOk(!infoSaisiesOk);
  };

  // VERIFIER DOSSIER
  const {
    mutate: verifierDossierLocation,
    isLoading: loadingVerifierDossierLocation,
    error: errorVerifierDossierLocation,
  } = useVerifierDossierLocation();

  const canSeeParcoursLocataire =
    adbDetail?.habilitations?.parcoursCandidatLocataire;
  return {
    getSynthese,
    resetSituationPro,
    addColoc,
    addLocataire,
    setSelectedPerson,
    resetSelectedPerson,
    resetParcours,
    calculLoyerMax,
    forcerStatutDossier,
    getPartenaire,
    isFetchingPartenaire,
    isFetchingSynthese,
    errorSynthese,
    synthese,
    selectedDossier,
    infoProfil,
    utilisateurConnecte,
    isControleDossier,
    partenaire,
    calculLoyerFetching,
    calculLoyerError,
    forcerStatutFetching,
    compositionLocation,
    verifierDossierLocation,
    loadingVerifierDossierLocation,
    errorVerifierDossierLocation,
    handleForcerStatutDossier,
    infoSaisiesOk,
    infoAVerifier,
    handleClickInfoSaisies,
    canSeeParcoursLocataire,
  };
};
